import styled from "@emotion/styled";
import { Button } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Container } from "@mui/system";
import Player from "@vimeo/player";
import { helpHttp } from "../../helper/helpHttp";
import { useUser } from "../../hooks/useUser";
import { toast } from "react-toastify";

// Módulo Delicake -> evaluación 35 / módulo 31 en el CMS.
const MODULE_ID = 31;

// La API exige registrar 5 respuestas sí o sí, pero solo publicamos 2 preguntas.
// Las otras 3 preguntas (130, 131, 132) quedan ocultas y enviamos una respuesta
// fija para completar las 5 requeridas.
//
// Para que el usuario solo apruebe acertando las 2 visibles, calibramos cuántas
// ocultas van correctas: enviamos 2 correctas + 1 incorrecta (a propósito), de
// modo que la línea de aprobación del backend caiga en "2/2 visibles":
//   2/2 visibles -> 2 + 2 ocultas = 4/5 (aprueba)
//   1/2 visibles -> 1 + 2 ocultas = 3/5 (no aprueba)
const HIDDEN_ANSWERS = [
  { idPregunta: "130", idRespuesta: "416", correcta: true },
  { idPregunta: "131", idRespuesta: "421", correcta: true },
  { idPregunta: "132", idRespuesta: "427", correcta: false }, // incorrecta a propósito (la correcta es 426)
];
const HIDDEN_QUESTION_IDS = HIDDEN_ANSWERS.map((h) => h.idPregunta);
const HIDDEN_CORRECT_COUNT = HIDDEN_ANSWERS.filter((h) => h.correcta).length;
const REQUIRED_ANSWER_COUNT = 5;

export const Delicake = () => {
  const [preguntas, setPreguntas] = useState([]);
  const [idEvaluacion, setIdEvaluacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estadoModulo, setEstadoModulo] = useState({
    yaAprobo: false,
    puedeReintentar: true,
    puedeReintentarEn24h: false,
    sinIntentos: false,
  });
  const [respuestas, setRespuestas] = useState({});
  const [resultado, setResultado] = useState(null);
  const [errorEnvio, setErrorEnvio] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const { post, get } = helpHttp();
  const { user } = useUser();
  const Capsula1 = useRef(null);

  const quizBloqueado = !estadoModulo.puedeReintentar && resultado === null;

  const actualizarEstadoModulo = (modulo) => {
    if (!modulo) return;
    const realizados = Number(modulo.intentos_realizados);
    const configurados = Number(modulo.intentos_configurados);
    const intentar = Number(modulo.intentar);
    setEstadoModulo({
      yaAprobo: String(modulo.aprobado) === "1",
      puedeReintentar: intentar === 1,
      puedeReintentarEn24h: realizados < configurados && intentar === 0,
      sinIntentos: realizados >= configurados,
    });
  };

  const handleRespuesta = (preguntaIndex, opcionIndex) => {
    if (resultado || quizBloqueado) return;
    setRespuestas((prev) => ({ ...prev, [preguntaIndex]: opcionIndex }));
  };

  const handleEnviar = () => {
    if (quizBloqueado) return;
    if (Object.keys(respuestas).length < preguntas.length) return;

    const respuestasPayload = preguntas.map((p, i) => ({
      idPregunta: p.idPregunta,
      idRespuesta: p.opciones[respuestas[i]].idRespuesta,
    }));

    const payload = {
      idEvaluacion: idEvaluacion,
      respuestas: [
        ...respuestasPayload,
        ...HIDDEN_ANSWERS.map((h) => ({
          idPregunta: h.idPregunta,
          idRespuesta: h.idRespuesta,
        })),
      ],
    };

    if (payload.respuestas.length !== REQUIRED_ANSWER_COUNT) return;

    setEnviando(true);
    post("evaluaciones/registrarEvaluacion", { body: payload })
      .then((data) => {
        if (data?.codigo === "200") {
          setResultado(data.datos);
          get(`modulos/getModulosByIdAndUsuarioCampaign/${MODULE_ID}`, {
            headers: { Authorization: user.token },
          })
            .then((modData) => actualizarEstadoModulo(modData?.datos?.[0]))
            .catch(() => {});
        } else {
          setErrorEnvio(
            data?.mensaje ?? "Ha ocurrido un error al registrar la evaluación",
          );
        }
      })
      .catch((err) => {
        setErrorEnvio(
          err?.message ?? "Ha ocurrido un error al registrar la evaluación",
        );
      })
      .finally(() => setEnviando(false));
  };

  const handleReintentar = () => {
    setRespuestas({});
    setResultado(null);
    setErrorEnvio(null);
  };

  // resultado.exitosas incluye las respuestas ocultas que van correctas,
  // así que las restamos para mostrar solo los aciertos de las preguntas visibles.
  const exitosasVisibles = resultado
    ? Math.max(
        0,
        Math.min(
          (resultado.exitosas ?? 0) - HIDDEN_CORRECT_COUNT,
          preguntas.length,
        ),
      )
    : 0;

  useEffect(() => {
    const capsula1Player = new Player(Capsula1.current);

    const fetchModulo = get(
      `modulos/getModulosByIdAndUsuarioCampaign/${MODULE_ID}`,
      {
        headers: { Authorization: user.token },
      },
    ).then((data) => {
      const modulo = data?.datos?.[0];
      if (modulo) {
        if (modulo.evaluacion) setIdEvaluacion(modulo.evaluacion);
        actualizarEstadoModulo(modulo);
      }
    });

    const fetchPreguntas = get(
      `evaluaciones/getEvaluacionByModulo/${MODULE_ID}`,
      {
        headers: { Authorization: user.token },
      },
    ).then((data) => {
      if (data?.codigo === "200" && Array.isArray(data.datos)) {
        const mapped = data.datos
          .filter(
            (item) => !HIDDEN_QUESTION_IDS.includes(String(item.idPregunta)),
          )
          .map((item) => ({
            idPregunta: item.idPregunta,
            pregunta: item.pregunta,
            opciones: item.respuestas,
          }));
        setPreguntas(mapped);
      }
    });

    Promise.allSettled([fetchModulo, fetchPreguntas])
      .then((results) => {
        if (results.some((r) => r.status === "rejected")) {
          toast.error("Ha ocurrido un error al cargar el contenido");
        }
      })
      .finally(() => setLoading(false));

    capsula1Player.on("ended", () => {});
    return () => {
      capsula1Player.off("ended");
    };
  }, []);

  return (
    <StyledContainer>
      <Container>
        <StyledMain>
          <StyledVideo>
            <iframe
              ref={Capsula1}
              width="100%"
              height="100%"
              src="https://player.vimeo.com/video/1214644414"
              frameBorder="0"
              allow="fullscreen; picture-in-picture"
              title="Delicake Introducción"
              loading="lazy"
            />
          </StyledVideo>

          {loading ? (
            <StyledQuiz>
              <p>Cargando cuestionario...</p>
            </StyledQuiz>
          ) : estadoModulo.yaAprobo ? (
            <StyledAprobadoCard>
              <StyledAprobadoIcon color="green">✓</StyledAprobadoIcon>
              <h3>¡Ya aprobaste esta evaluación!</h3>
              <p style={{ margin: 0, color: "#773d1e", fontSize: "1.6rem" }}>
                Respondiste exitosamente, prepárate para ganar. !Mucha suerte!
              </p>
            </StyledAprobadoCard>
          ) : estadoModulo.sinIntentos ? (
            <StyledAprobadoCard>
              <StyledAprobadoIcon color="red">✕</StyledAprobadoIcon>
              <h3>Sin intentos disponibles</h3>
              <p style={{ margin: 0, color: "#773d1e", fontSize: "1.6rem" }}>
                Has utilizado todos los intentos permitidos para esta
                evaluación. Ya no es posible volver a realizarla.
              </p>
            </StyledAprobadoCard>
          ) : estadoModulo.puedeReintentarEn24h && resultado === null ? (
            <StyledAprobadoCard>
              <StyledAprobadoIcon color="orange">⏱</StyledAprobadoIcon>
              <h3>Intento en espera</h3>
              <p style={{ margin: 0, color: "#773d1e", fontSize: "1.6rem" }}>
                Podrás volver a intentar esta evaluación en las próximas 24
                horas. ¡Revisa el video y prepárate!
              </p>
            </StyledAprobadoCard>
          ) : (
            <StyledQuiz>
              <p style={{ margin: 0, color: "#114c97", fontSize: "1.6rem" }}>
                Conoce nuestra nueva línea de <b>Premezclas Delicake,</b> mira el vídeo y Podrás participar en el sorteo de una <b>kitchenAid</b>
              </p>
              <br />
              {preguntas.length === 0 ? (
                <p>No se pudo cargar el cuestionario.</p>
              ) : (
                <>
                  {preguntas.map((item, pi) => (
                    <StyledPregunta key={item.idPregunta}>
                      <p>
                        <strong>
                          {pi + 1}. {item.pregunta}
                        </strong>
                      </p>
                      <StyledOpciones>
                        {item.opciones.map((opcion, oi) => {
                          const seleccionada = respuestas[pi] === oi;
                          return (
                            <StyledOpcion
                              key={opcion.idRespuesta}
                              estado={seleccionada ? "seleccionada" : "default"}
                              onClick={() => handleRespuesta(pi, oi)}
                            >
                              <input
                                type="radio"
                                name={`pregunta-${pi}`}
                                checked={seleccionada}
                                readOnly
                              />
                              <strong>{String.fromCharCode(65 + oi)})</strong>
                              &nbsp;{opcion.respuesta}
                            </StyledOpcion>
                          );
                        })}
                      </StyledOpciones>
                    </StyledPregunta>
                  ))}

                  {errorEnvio ? (
                    <StyledResultado>
                      <p style={{ color: "#d32f2f" }}>
                        Ha superado el máximo número de intentos.
                      </p>
                    </StyledResultado>
                  ) : !resultado ? (
                    <Button
                      variant="contained"
                      onClick={handleEnviar}
                      disabled={
                        quizBloqueado ||
                        Object.keys(respuestas).length < preguntas.length ||
                        enviando
                      }
                      sx={{
                        alignSelf: "flex-start",
                        mt: 1,
                        backgroundColor: "#773d1e",
                        "&:hover": {
                          backgroundColor: "#b86e3a",
                        },
                      }}
                    >
                      {enviando ? "Enviando..." : "Enviar respuestas"}
                    </Button>
                  ) : (
                    <StyledResultado>
                      <p
                        style={{
                          fontSize: "1.4rem",
                          margin: "auto",
                          color: "#773d1e",
                        }}
                      >
                        Obtuviste <strong>{exitosasVisibles}</strong> de{" "}
                        <strong>{preguntas.length}</strong> respuestas
                        correctas.
                      </p>
                      <p
                        style={{
                          fontSize: "1.4rem",
                          margin: "auto",
                          color: "#773d1e",
                        }}
                      >
                        {resultado.aprobado
                          ? "¡Felicitaciones, aprobaste el cuestionario!"
                          : "No aprobaste el cuestionario."}
                      </p>
                      {resultado.reintentar && (
                        <Button variant="outlined" onClick={handleReintentar}>
                          Intentar nuevamente
                        </Button>
                      )}
                    </StyledResultado>
                  )}
                </>
              )}
            </StyledQuiz>
          )}
        </StyledMain>
        <StyledTerminos>
          <a
            href="/images/lanzamientos/Terminosycondiciones-Delicake.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ver términos y condiciones
          </a>
        </StyledTerminos>
      </Container>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  background-color: #e5e5e5;
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
`;

const StyledMain = styled.div`
  padding-top: 120px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledVideo = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
`;

const StyledQuiz = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  h2 {
    margin: 0;
    font-size: 1.4rem;
    color: #222;
  }
`;

const StyledPregunta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  p {
    margin: 0;
    font-size: 1rem;
    color: #333;
  }
`;

const StyledOpciones = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const colorMap = {
  default: { bg: "#f5f5f5", border: "#ccc", text: "#333" },
  seleccionada: { bg: "#e3f2fd", border: "#1976d2", text: "#1976d2" },
};

const StyledOpcion = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background 0.2s;
  background-color: ${({ estado }) =>
    colorMap[estado]?.bg ?? colorMap.default.bg};
  border: 1.5px solid
    ${({ estado }) => colorMap[estado]?.border ?? colorMap.default.border};
  color: ${({ estado }) => colorMap[estado]?.text ?? colorMap.default.text};

  input {
    accent-color: currentColor;
    cursor: pointer;
  }
`;

const StyledResultado = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  p {
    margin: 0;
    font-size: 1.1rem;
    color: #333;
  }
`;

const StyledAprobadoCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: #fff;
  border-radius: 12px;
  padding: 40px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  text-align: center;

  h3 {
    margin: 0;
    font-size: 1.4rem;
    color: #333;
  }

  p {
    margin: 0;
    color: #555;
    font-size: 1rem;
  }
`;

const colorIconMap = {
  green: { bg: "#e8f5e9", text: "#2e7d32" },
  red: { bg: "#ffebee", text: "#c62828" },
  orange: { bg: "#fff3e0", text: "#e65100" },
};

const StyledAprobadoIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: ${({ color }) =>
    colorIconMap[color]?.bg ?? colorIconMap.green.bg};
  color: ${({ color }) => colorIconMap[color]?.text ?? colorIconMap.green.text};
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

const StyledTerminos = styled.div`
  padding: 16px 0 8px;
  text-align: center;

  a {
    color: #555;
    font-size: 0.85rem;
    text-decoration: underline;

    &:hover {
      color: #222;
    }
  }
`;
